import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useContextMenu } from "../Layout Component/ContextMenu";
import { VITAL_TYPES, addEntries, removeEntry, syncQuestionnaire, today } from "./vitalsStore";

// Vitals: what the clinical Observation Results page shows first (height,
// weight, BMI, blood pressure, oxygen saturation) as tiles with the latest
// reading, and a form so the patient can record their own. Readings the
// patient entered are marked "entered by you". See vitalsStore.js for where
// they are kept.

function latestOf(entries, type) {
  return entries.filter((e) => e.type === type).sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
}

function bmiBand(b) {
  return b < 18.5 ? "Underweight" : b < 25 ? "Normal" : b < 30 ? "Overweight" : "Obese";
}

export default function VitalsPanel({ entries, onChange, clinicWeight, clinicHeight }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("weight");
  const [values, setValues] = useState({});
  const [when, setWhen] = useState(today());
  const [busy, setBusy] = useState(false);
  const { show, menu } = useContextMenu();

  const spec = VITAL_TYPES.find((t) => t.key === type) || VITAL_TYPES[0];
  const fields = spec.group === "bp" ? VITAL_TYPES.filter((t) => t.group === "bp") : [spec];

  useEffect(() => { setValues({}); }, [type]);

  const tiles = useMemo(() => {
    const w = latestOf(entries, "weight");
    const h = latestOf(entries, "height");
    const weight = w ? Number(w.value) : Number(clinicWeight) || null;
    const height = h ? Number(h.value) : Number(clinicHeight) || null;
    const sys = latestOf(entries, "bpSystolic");
    const dia = latestOf(entries, "bpDiastolic");
    const bmi = weight > 0 && height > 0 ? weight / (height * height) : null;
    const tile = (key, label, text, unit, date, mine) => ({ key, label, text, unit, date, mine });
    const own = (key, label) => {
      const e = latestOf(entries, key);
      return e ? tile(key, label, e.value, VITAL_TYPES.find((v) => v.key === key)?.unit, e.date, true) : null;
    };
    return [
      own("weight", "Weight") || (weight ? tile("weight", "Weight", weight, "kg", null, false) : null),
      own("height", "Height") || (height ? tile("height", "Height", height, "m", null, false) : null),
      bmi ? tile("bmi", "BMI (calculated)", bmi.toFixed(1), bmiBand(bmi), null, false) : null,
      sys && dia ? tile("bp", "Blood pressure", `${sys.value}/${dia.value}`, "mmHg", sys.date, true) : null,
      own("oxygen", "Oxygen saturation"),
      own("heartRate", "Heart rate"),
      own("temperature", "Temperature"),
    ].filter(Boolean);
  }, [entries, clinicWeight, clinicHeight]);

  async function submit(e) {
    e.preventDefault();
    const list = fields.map((f) => ({ f, v: Number(values[f.key]) }));
    const bad = list.find(({ f, v }) => !Number.isFinite(v) || v < f.min || v > f.max);
    if (bad) {
      toast.warn(`${bad.f.label} should be between ${bad.f.min} and ${bad.f.max} ${bad.f.unit}`);
      return;
    }
    try {
      setBusy(true);
      const date = new Date(when).toISOString();
      const next = addEntries(list.map(({ f, v }) => ({ type: f.key, value: v, date })));
      onChange(next);
      if (type === "weight" || type === "height") {
        // Best effort: the entry is kept either way.
        await syncQuestionnaire({ weight: type === "weight" ? list[0].v : null, height: type === "height" ? list[0].v : null })
          .catch(() => toast.info("Saved here. The clinic's record could not be updated just now."));
      }
      toast.success(`${spec.group === "bp" ? "Blood pressure" : spec.label} recorded`, { autoClose: 2500, hideProgressBar: true });
      setValues({});
      setWhen(today());
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "That could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  const recordFor = (key) => { setType(key); setOpen(true); };
  const mine = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8);

  return (
    <section
      className="ox-vitals"
      aria-label="Vitals"
      onContextMenu={(e) => show(e, [
        { label: "Record my weight", onClick: () => recordFor("weight") },
        { label: "Record blood pressure", onClick: () => recordFor("bpSystolic") },
        { label: "Record another vital...", onClick: () => recordFor("oxygen") },
      ])}
    >
      <header className="ox-vitals__head">
        <h3>Vitals</h3>
        <button type="button" className="ox-btn ox-btn--primary ox-btn--sm" onClick={() => setOpen((o) => !o)}>
          {open ? "Close" : "+ Record a vital"}
        </button>
      </header>

      {tiles.length === 0 ? (
        <p className="ox-hint">No vitals yet. Record your weight or blood pressure to start a graph.</p>
      ) : (
        <div className="ox-vitals__tiles">
          {tiles.map((t) => (
            <div
              key={t.key}
              className="ox-vital ox-clickable"
              tabIndex={0}
              title="Double-click to record a new reading"
              onDoubleClick={() => recordFor(t.key === "bp" ? "bpSystolic" : t.key === "bmi" ? "weight" : t.key)}
              onKeyDown={(e) => e.key === "Enter" && recordFor(t.key === "bp" ? "bpSystolic" : t.key === "bmi" ? "weight" : t.key)}
            >
              <span className="ox-vital__label">{t.label}</span>
              <strong className="ox-vital__value">{t.text}</strong>
              <span className="ox-vital__unit">{t.unit}</span>
              {t.date && <span className="ox-vital__when">{moment(t.date).format("DD MMM YYYY")}{t.mine ? " · entered by you" : ""}</span>}
            </div>
          ))}
        </div>
      )}

      {open && (
        <form className="ox-vitals__form" onSubmit={submit}>
          <label>
            What are you recording?
            <select className="form-select" value={type === "bpDiastolic" ? "bpSystolic" : type} onChange={(e) => setType(e.target.value)}>
              {VITAL_TYPES.filter((t) => t.key !== "bpDiastolic").map((t) => (
                <option key={t.key} value={t.key}>{t.group === "bp" ? "Blood pressure" : t.label}</option>
              ))}
            </select>
          </label>
          {fields.map((f) => (
            <label key={f.key}>
              {fields.length > 1 ? (f.key === "bpSystolic" ? "Systolic (upper)" : "Diastolic (lower)") : "Reading"} ({f.unit})
              <input
                className="form-control"
                type="number"
                inputMode="decimal"
                step={f.step}
                min={f.min}
                max={f.max}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                required
              />
            </label>
          ))}
          <label>
            When
            <input className="form-control" type="datetime-local" value={when} max={today()} onChange={(e) => setWhen(e.target.value)} required />
          </label>
          <div className="ox-vitals__go">
            <button type="submit" className="ox-btn ox-btn--primary" disabled={busy}>{busy ? "Saving..." : "Save reading"}</button>
          </div>
        </form>
      )}

      {mine.length > 0 && (
        <details className="ox-vitals__list">
          <summary>Readings you have entered ({entries.length})</summary>
          <ul>
            {mine.map((e) => {
              const t = VITAL_TYPES.find((v) => v.key === e.type);
              return (
                <li key={e.id}>
                  <span>{t.label}: <strong>{e.value} {t.unit}</strong> &middot; {moment(e.date).format("DD MMM YYYY HH:mm")}</span>
                  <button type="button" className="ox-link" onClick={() => onChange(removeEntry(e.id))} aria-label={`Remove ${t.label} reading`}>Remove</button>
                </li>
              );
            })}
          </ul>
          <p className="ox-hint">Readings you enter are kept on this device. Your weight and height are also sent to your clinic's record.</p>
        </details>
      )}
      {menu}
    </section>
  );
}
